import {
  DefaultTimelineExclusion,
  LabellerDetails,
  LabellerTimeline,
  TimelineFeature,
} from "src/store/api/generatedApi";

/**
 * Labelling-period ids to keep out of the merge, per feature the exclusion covers: `all`
 * drops the labeller's events entirely, `material` drops only their material votes,
 * `timing` only their contribution to the merged start and end. Keyed by the generated
 * `TimelineFeature` union, so a feature added to the API cannot be silently ignored here.
 */
export type TimelineExclusionPeriodIds = Record<TimelineFeature, Set<number>>;

export const TIMELINE_FEATURES: readonly TimelineFeature[] = [
  "all",
  "material",
  "timing",
];

const matchesLabeller = (
  exclusion: DefaultTimelineExclusion,
  { labeller }: LabellerTimeline
): boolean =>
  labeller.labeller_type === exclusion.labeller_type &&
  ((exclusion.model_status ?? null) === null ||
    labeller.model_status === exclusion.model_status);

const periodIdsForFeature = ({
  labellerTimelines,
  defaultTimelineExclusions,
  feature,
}: {
  labellerTimelines: LabellerTimeline[];
  defaultTimelineExclusions: DefaultTimelineExclusion[];
  feature: TimelineFeature;
}): Set<number> =>
  new Set(
    defaultTimelineExclusions
      .filter((exclusion) => exclusion.feature === feature)
      .flatMap((exclusion) =>
        labellerTimelines
          .filter((timeline) => matchesLabeller(exclusion, timeline))
          .flatMap((timeline) =>
            timeline.periods.map((period) => period.period_id)
          )
      )
  );

/**
 * The labelling periods a review surface excludes from its merge before the reviewer
 * touches anything: every period belonging to a labeller a `DefaultTimelineExclusion`
 * names. An exclusion with no `model_status` matches any labeller of its type.
 */
export const defaultExclusionPeriodIds = ({
  labellerTimelines,
  defaultTimelineExclusions,
}: {
  labellerTimelines: LabellerTimeline[] | undefined;
  defaultTimelineExclusions: DefaultTimelineExclusion[] | undefined;
}): TimelineExclusionPeriodIds =>
  Object.fromEntries(
    TIMELINE_FEATURES.map((feature) => [
      feature,
      periodIdsForFeature({
        labellerTimelines: labellerTimelines ?? [],
        defaultTimelineExclusions: defaultTimelineExclusions ?? [],
        feature,
      }),
    ])
  ) as TimelineExclusionPeriodIds;

/** Identifies a labeller across windows, since labelling-period ids do not carry over. */
export const labellerKey = (labeller: LabellerDetails): string =>
  `${labeller.labeller_type}:${labeller.id}`;

export type LabellerExclusionDecisions = ReadonlyMap<
  string,
  Record<TimelineFeature, boolean>
>;

/**
 * Which labellers a surface currently has excluded, per feature — its defaults as reported,
 * plus whatever the reviewer has since toggled. A lane is excluded when every one of its
 * labelling periods is, matching how the lane controls act on all of a labeller's periods
 * at once.
 */
export const labellerExclusionDecisions = ({
  labellerTimelines,
  excludedPeriodIds,
}: {
  labellerTimelines: LabellerTimeline[] | undefined;
  excludedPeriodIds: TimelineExclusionPeriodIds;
}): LabellerExclusionDecisions =>
  new Map(
    (labellerTimelines ?? []).map((timeline) => {
      const periodIds = timeline.periods.map((period) => period.period_id);
      const allExcluded = (feature: TimelineFeature): boolean =>
        periodIds.length > 0 &&
        periodIds.every((periodId) => excludedPeriodIds[feature].has(periodId));
      return [
        labellerKey(timeline.labeller),
        {
          all: allExcluded("all"),
          material: allExcluded("material"),
          timing: allExcluded("timing"),
        },
      ];
    })
  );

/**
 * A surface's own labelling periods to exclude, given the decisions an ancestor surface has
 * made per labeller. Decisions are inherited rather than re-derived so the two surfaces keep
 * the same labellers out of the region they share, including a default the reviewer put back
 * in view; mapping them onto this window's own period ids is what carries them across, since
 * the ids themselves do not.
 */
export const exclusionPeriodIdsFromDecisions = ({
  labellerTimelines,
  decisions,
}: {
  labellerTimelines: LabellerTimeline[] | undefined;
  decisions: LabellerExclusionDecisions;
}): TimelineExclusionPeriodIds =>
  Object.fromEntries(
    TIMELINE_FEATURES.map((feature) => [
      feature,
      new Set(
        (labellerTimelines ?? [])
          .filter(
            (timeline) =>
              decisions.get(labellerKey(timeline.labeller))?.[feature]
          )
          .flatMap((timeline) =>
            timeline.periods.map((period) => period.period_id)
          )
      ),
    ])
  ) as TimelineExclusionPeriodIds;
